"use client";

import { ArrowRight, Bird, Anchor, Waves } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Protection() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const disciplines = [{
    title: "Protection Island",
    subtitle: "NATIONAL WILDLIFE REFUGE",
    icon: "bird",
    description: "A critical sanctuary for nesting seabirds and marine mammals. Learn how we monitor populations and maintain the integrity of this vital island ecosystem.",
    skills: ["Seabird nesting habitat monitoring", "Marine mammal protection zones", "Invasive species management", "Scientific research support"],
    popular: false
  }, {
    title: "Dungeness Refuge",
    subtitle: "NATIONAL WILDLIFE REFUGE",
    icon: "anchor",
    description: "Home to the iconic Dungeness Spit, this refuge provides essential resting and feeding grounds for migratory birds and diverse coastal wildlife species.",
    skills: ["Migratory bird path preservation", "Coastal erosion mitigation", "Public education & trail safety", "Habitat restoration projects"],
    popular: true
  }, {
    title: "Wildlife Advocacy",
    subtitle: "PUBLIC EDUCATION",
    icon: "waves",
    description: "Empowering the public to become stewards of our natural heritage. Discover how your voice and actions directly impact the survival of our local sea birds.",
    skills: ["Community outreach programs", "Wildlife conservation workshops", "Advocacy & policy awareness", "Volunteer engagement initiatives"],
    popular: false
  }];
  const renderIcon = (icon: string) => {
    if (icon === "bird") return <Bird className="w-20 h-20 text-blue-600" strokeWidth={1.25} />;
    if (icon === "anchor") return <Anchor className="w-20 h-20 text-blue-600" strokeWidth={1.25} />;
    return <Waves className="w-20 h-20 text-blue-600" strokeWidth={1.25} />;
  };
  return <section data-section-id="244" id="ways-to-protect-preserve" className="bg-slate-50 text-gray-900 pt-24 md:pt-32 lg:pt-40 pb-20 md:pb-24 lg:pb-30">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div ref={header.ref} className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs md:text-sm tracking-[0.2em] uppercase text-blue-600 mb-6">
            Conservation / Focus Areas
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight mb-6">
            Ways to Protect &{" "}
            <span className="italic text-green-700 font-light">Preserve</span>
          </h2>
          <p className="font-default text-base md:text-lg text-gray-700 max-w-[600px] mx-auto leading-relaxed">
            From habitat monitoring to public advocacy — explore our core initiatives and find the path that resonates with your commitment to wildlife.
          </p>
        </div>

        <div ref={grid.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-700 ease-out delay-150 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {disciplines.map((item, i) => <div data-index={i} key={item.title} className="group relative flex flex-col bg-white border border-gray-200 rounded-lg p-10 transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-lg overflow-hidden">
              {item.popular && <Badge className="absolute top-6 right-6 bg-blue-600 text-white font-default text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-lg shadow-sm z-10">
                  Priority
                </Badge>}

              <div className="aspect-square w-full bg-green-50 rounded-lg flex items-center justify-center mb-8 overflow-hidden transition-transform duration-500 ease-out group-hover:scale-[1.02]">
                <div className="transition-transform duration-500 ease-out group-hover:scale-110">
                  {renderIcon(item.icon)}
                </div>
              </div>

              <div className="mb-5">
                <h3 className="font-default text-[40px] leading-[1.05] font-bold text-gray-900 mb-2 tracking-tight">
                  {item.title}
                </h3>
                <p className="font-default text-xs tracking-[0.2em] uppercase text-blue-600">
                  {item.subtitle}
                </p>
              </div>

              <p className="font-default text-base text-gray-700 leading-relaxed mb-6">
                {item.description}
              </p>

              <div className="relative h-px bg-blue-600/40 w-16 mb-6 transition-all duration-500 ease-out group-hover:w-full" />

              <ul className="flex flex-col gap-3 mb-8">
                {item.skills.map((skill, j) => <li data-index={j} key={skill} className="flex items-start gap-3 font-default text-sm text-gray-800">
                    <span className="mt-2 w-1.5 h-1.5 rounded-lg bg-green-700 shrink-0" />
                    <span className="leading-relaxed">{skill}</span>
                  </li>)}
              </ul>

              <div className="mt-auto">
                <Button asChild variant="link" className="p-0 h-auto font-default text-xs tracking-[0.2em] uppercase text-gray-900 hover:text-blue-600 no-underline hover:no-underline">
                  <Link to="/AdvocacyEducationPage" className="inline-flex items-center gap-2">
                    Learn More
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}